import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';

import { map } from 'rxjs';
import { ICellEditorParams } from 'ag-grid-enterprise';
import { ICellEditorAngularComp } from 'ag-grid-angular';
import { CellEditingStoppedEvent } from 'ag-grid-community';
import { TsButtonModule } from '../../../ts-button/ts-button.module';
import { latitudeValidator, longitudeValidator } from '../../../../utils/validators/coordinate-validators';

@Component({
  standalone: true,
  imports: [ReactiveFormsModule, MatFormFieldModule, MatInputModule, TsButtonModule],
  template: `
    <section class="bg-white p-4 min-w-56 shadow-md" [style]="{ width: width }">
      <form [formGroup]="coordinatesForm" class="flex flex-col">
        <mat-form-field class="w-full">
          <mat-label>Latitude:</mat-label>
          <input
            matInput
            type="number"
            formControlName="latitude"
            placeholder="Enter latitude (-90 to 90)"
            step="any"
            autofocus
          />
          @if (coordinatesForm.get('latitude')?.hasError('validLatitude') && coordinatesForm.get('latitude')?.touched) {
            <mat-error>Please enter a valid latitude between -90 and 90</mat-error>
          }
        </mat-form-field>
        <mat-form-field class="w-full">
          <mat-label>Longitude:</mat-label>
          <input
            matInput
            type="number"
            formControlName="longitude"
            placeholder="Enter longitude (-180 to 180)"
            step="any"
          />
          @if (
            coordinatesForm.get('longitude')?.hasError('validLongitude') && coordinatesForm.get('longitude')?.touched
          ) {
            <mat-error>Please enter a valid longitude between -180 and 180</mat-error>
          }
        </mat-form-field>
      </form>
      <article class="flex justify-end gap-2">
        <ts-button variant="secondary" (click)="cancel()">Cancel</ts-button>
        <ts-button variant="primary" (click)="save()" [disabled]="!coordinatesForm.valid || !hasChanges()">
          Save
        </ts-button>
      </article>
    </section>
  `,
})
export class TsAgCoordinatesEditor implements ICellEditorAngularComp {
  params: ICellEditorParams = null;
  width = 'auto';
  hasChanges = signal<boolean>(false);
  coordinatesForm = new FormGroup({
    latitude: new FormControl<number | null>(null, [Validators.required, latitudeValidator]),
    longitude: new FormControl<number | null>(null, [Validators.required, longitudeValidator]),
  });

  private originalValues: { latitude: number | null; longitude: number | null } = { latitude: null, longitude: null };
  private editedFieldName: string = '';

  private readonly destroyRef = inject(DestroyRef);

  agInit(params: ICellEditorParams): void {
    this.params = params;
    this.width = params.column.getActualWidth() + 'px';
    this.editedFieldName = params.colDef?.field || params.colDef?.colId || '';

    const rowData = params.data || {};

    let latitude: number | null = null;
    let longitude: number | null = null;

    if (rowData.latitude) {
      latitude = parseFloat(rowData.latitude);
    } else if (rowData.address?.latitude) {
      latitude = parseFloat(rowData.address.latitude);
    }

    if (rowData.longitude) {
      longitude = parseFloat(rowData.longitude);
    } else if (rowData.address?.longitude) {
      longitude = parseFloat(rowData.address.longitude);
    }

    if (params.value) {
      const parsedValue = parseFloat(params.value);
      if (!isNaN(parsedValue)) {
        if (this.editedFieldName === 'latitude' || this.editedFieldName.includes('latitude')) {
          latitude = parsedValue;
        } else if (this.editedFieldName === 'longitude' || this.editedFieldName.includes('longitude')) {
          longitude = parsedValue;
        }
      }
    }

    latitude = isNaN(latitude) ? null : latitude;
    longitude = isNaN(longitude) ? null : longitude;

    this.originalValues = {
      latitude,
      longitude,
    };

    this.coordinatesForm.patchValue(
      {
        latitude,
        longitude,
      },
      { emitEvent: false },
    );

    this.setupFormValueChanges();
  }

  isPopup(): boolean {
    return true;
  }

  getPopupPosition?(): 'over' | 'under' | undefined {
    return 'under';
  }

  private setupFormValueChanges(): void {
    this.coordinatesForm.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map((formValue) => {
          const currentLat = formValue.latitude ?? null;
          const currentLng = formValue.longitude ?? null;
          const initialLat = this.originalValues.latitude;
          const initialLng = this.originalValues.longitude;

          return currentLat !== initialLat || currentLng !== initialLng;
        }),
      )
      .subscribe((hasChanges) => {
        this.hasChanges.set(hasChanges);
      });
  }

  save(): void {
    if (this.coordinatesForm.valid) {
      const formValue = this.coordinatesForm.value;
      const latitude = formValue.latitude ?? null;
      const longitude = formValue.longitude ?? null;

      const otherFieldName = this.editedFieldName === 'latitude' ? 'longitude' : 'latitude';
      const otherFieldValue = this.editedFieldName === 'latitude' ? longitude : latitude;
      const otherOriginalValue =
        this.editedFieldName === 'latitude' ? this.originalValues.longitude : this.originalValues.latitude;

      const clickedFieldValue = this.editedFieldName === 'latitude' ? latitude : longitude;
      const clickedFieldOriginalValue =
        this.editedFieldName === 'latitude' ? this.originalValues.latitude : this.originalValues.longitude;
      const clickedFieldChanged = clickedFieldValue !== clickedFieldOriginalValue;

      const otherOldValue =
        this.params.node?.data?.[otherFieldName] ?? this.params.node?.data?.address?.[otherFieldName] ?? null;

      if (this.params.node && otherFieldValue !== null && otherFieldValue !== undefined) {
        const otherColumn = this.params.api?.getColumn(otherFieldName);
        if (otherColumn) {
          this.params.node.setDataValue(otherFieldName, otherFieldValue);
        }
      }

      this.params.stopEditing();

      // After stopEditing(), manually trigger cellEditingStopped for the other field ONLY if:
      // 1. The other field changed AND
      // 2. The clicked field did NOT change (to avoid duplicate backend calls)
      // If the clicked field changed, its event will already trigger a backend call with updated row data
      if (this.params.node && otherFieldValue !== null && otherFieldValue !== undefined) {
        const otherColumn = this.params.api?.getColumn(otherFieldName);
        const otherFieldChanged = otherFieldValue !== otherOriginalValue;

        // Only dispatch event for other field if it changed AND clicked field didn't change
        if (otherColumn && otherFieldChanged && !clickedFieldChanged) {
          // Manually construct and dispatch a CellEditingStoppedEvent for the other field
          const otherFieldEvent = {
            type: 'cellEditingStopped',
            api: this.params.api,
            context: this.params.context,
            data: this.params.data,
            node: this.params.node,
            oldValue: otherOldValue,
            newValue: otherFieldValue,
            value: otherFieldValue,
            valueChanged: otherFieldValue !== otherOldValue,
            colDef: otherColumn.getColDef(),
            column: otherColumn,
            rowIndex: this.params.node.rowIndex ?? -1,
            rowPinned: this.params.node.rowPinned,
            source: 'api',
          } as unknown as CellEditingStoppedEvent;

          // Dispatch the event through ag-grid's event system
          // This will trigger onCellEditingStopped in ts-table.component.ts
          this.params.api.dispatchEvent(otherFieldEvent);

          // Also refresh the cell to ensure it displays correctly
          this.params.api.refreshCells({
            rowNodes: [this.params.node],
            columns: [otherFieldName],
            force: true,
          });
        }
      }

      const rowData = this.params.data || {};

      if (rowData.latitude !== undefined) {
        rowData.latitude = latitude;
      } else if (rowData.address) {
        rowData.address.latitude = latitude;
      }

      if (rowData.longitude !== undefined) {
        rowData.longitude = longitude;
      } else if (rowData.address) {
        rowData.address.longitude = longitude;
      }
    }
  }

  cancel(): void {
    this.params.stopEditing();
  }

  getValue(): any {
    const fieldName =
      this.editedFieldName === 'latitude' || this.editedFieldName.includes('latitude') ? 'latitude' : 'longitude';

    const value = this.coordinatesForm.get(fieldName)?.value ?? null;

    return value !== null && isNaN(value) ? null : value;
  }
}
